import type { Role } from './auth';
import { z } from 'zod';
import { Role as RoleConst } from './auth';
import { CURRENCY_CODES, currencyCodeSchema } from './currency';

// ---------------------------------------------------------------------------
// Eleggia DMC Partner Profile — shared schemas
// (apps/auth/prd/dmc-partner-profile.md, Phase 1).
//
// Entity (read) schemas mirror the Drizzle tables in
// packages/db/src/schema/organizations.ts (`dmc*InOrganizations`). Input schemas
// drive the form's single "save whole profile" mutation. Picklist fields are
// validated here as enums (the DB stores them as `text`) per the extensibility
// NFR. Timestamps round-trip as ISO strings (`mode: 'string'` columns).
// ---------------------------------------------------------------------------

// --- Picklist enums (controlled vocabularies) ------------------------------

// Field access tier (spec §5). Metadata, not yet enforced in Phase 1.
export const dmcAccessTierValues = ['L1', 'L2', 'L3'] as const;
export const dmcAccessTierSchema = z.enum(dmcAccessTierValues);
export type DmcAccessTier = z.infer<typeof dmcAccessTierSchema>;

export const dmcMemberStatusValues = [
  'prospect',
  'onboarding',
  'active',
  'suspended',
  'exited',
] as const;
export const dmcMemberStatusSchema = z.enum(dmcMemberStatusValues);
export type DmcMemberStatus = z.infer<typeof dmcMemberStatusSchema>;

export const dmcMarketStatusValues = [
  'active',
  'developing',
  'paused',
] as const;
export const dmcMarketStatusSchema = z.enum(dmcMarketStatusValues);
export type DmcMarketStatus = z.infer<typeof dmcMarketStatusSchema>;

export const dmcClientRelationValues = ['actual', 'potential'] as const;
export const dmcClientRelationSchema = z.enum(dmcClientRelationValues);
export type DmcClientRelation = z.infer<typeof dmcClientRelationSchema>;

// 09 CLI — client type (spec §3: "agency / operator / corporate / …").
export const dmcClientTypeValues = [
  'Agency',
  'Operator',
  'Corporate',
  'Other',
] as const;
export const dmcClientTypeSchema = z.enum(dmcClientTypeValues);
export type DmcClientType = z.infer<typeof dmcClientTypeSchema>;

// 03 INS / 04 FIN — currency. Derived from the canonical CURRENCY_CATALOG so
// this form offers exactly what every other currency picker offers.
export const dmcCurrencyValues = CURRENCY_CODES;
export const dmcCurrencySchema = currencyCodeSchema;
export type DmcCurrency = string;

// 15 ELG — Xthory adoption status (spec §3 names the field; no value list given).
export const dmcXthoryStatusValues = ['Active', 'Inactive'] as const;
export const dmcXthoryStatusSchema = z.enum(dmcXthoryStatusValues);
export type DmcXthoryStatus = z.infer<typeof dmcXthoryStatusSchema>;

// 16 DOC — document category (types drawn from the doc-bearing modules the spec
// references: licenses, insurance, contracts, NDAs, marketing).
export const dmcDocumentCategoryValues = [
  'License',
  'Insurance',
  'Contract',
  'NDA',
  'Certificate',
  'Marketing',
  'Other',
] as const;
export const dmcDocumentCategorySchema = z.enum(dmcDocumentCategoryValues);
export type DmcDocumentCategory = z.infer<typeof dmcDocumentCategorySchema>;

export const dmcInsuranceTypeValues = [
  'Public Liability',
  'Professional Indemnity',
  'Travel',
  'Other',
] as const;
export const dmcInsuranceTypeSchema = z.enum(dmcInsuranceTypeValues);
export type DmcInsuranceType = z.infer<typeof dmcInsuranceTypeSchema>;

export const dmcLicenseTypeValues = [
  'Tourism License',
  'IATA',
  'Network',
  'Certification',
] as const;
export const dmcLicenseTypeSchema = z.enum(dmcLicenseTypeValues);
export type DmcLicenseType = z.infer<typeof dmcLicenseTypeSchema>;

export const dmcResponsibilityDomainValues = [
  'commercial',
  'operations',
  'finance',
  'marketing',
  'tech',
  'legal',
  'crisis',
] as const;
export const dmcResponsibilityDomainSchema = z.enum(
  dmcResponsibilityDomainValues
);
export type DmcResponsibilityDomain = z.infer<
  typeof dmcResponsibilityDomainSchema
>;

// --- JSONB sub-shapes (loose 1:1 bags on the parent) -----------------------

// 01 CIL — directors (small repeatable set stored as jsonb).
export const dmcDirectorSchema = z.object({
  fullName: z.string().optional(),
  role: z.string().optional(),
  email: z.string().optional(),
});
export type DmcDirector = z.infer<typeof dmcDirectorSchema>;

// 01 CIL — shareholding note (band/percentage; L3).
export const dmcShareholderSchema = z.object({
  name: z.string().optional(),
  share: z.string().optional(),
});
export type DmcShareholder = z.infer<typeof dmcShareholderSchema>;

// 07 RES — one team-member ref per responsibility domain.
export const dmcResponsibilityStructureSchema = z.object({
  commercial: z.string().nullable().optional(),
  operations: z.string().nullable().optional(),
  finance: z.string().nullable().optional(),
  marketing: z.string().nullable().optional(),
  tech: z.string().nullable().optional(),
  legal: z.string().nullable().optional(),
  crisis: z.string().nullable().optional(),
});
export type DmcResponsibilityStructure = z.infer<
  typeof dmcResponsibilityStructureSchema
>;

// 11 DIG — digital presence.
export const dmcDigitalPresenceSchema = z.object({
  website: z.string().optional(),
  instagram: z.string().optional(),
  facebook: z.string().optional(),
  linkedin: z.string().optional(),
  youtube: z.string().optional(),
  otherSocial: z.string().optional(),
  followerCounts: z.record(z.string(), z.number()).optional(),
  newsletterUrl: z.string().optional(),
  brandAssetsRef: z.string().optional(),
});
export type DmcDigitalPresence = z.infer<typeof dmcDigitalPresenceSchema>;

// 12 MPL — marketing plan & coordination.
export const dmcCoordinationRowSchema = z.object({
  activity: z.string().optional(),
  owner: z.string().optional(),
  notes: z.string().optional(),
});
export const dmcMarketingPlanSchema = z.object({
  planSummary: z.string().optional(),
  budgetBand: z.string().optional(), // L3
  papAlignment: z.string().optional(),
  coordinationMatrix: z.array(dmcCoordinationRowSchema).optional(),
});
export type DmcMarketingPlan = z.infer<typeof dmcMarketingPlanSchema>;

// 13 EMG — escalation step.
export const dmcEscalationStepSchema = z.object({
  level: z.string().optional(),
  contact: z.string().optional(),
  sla: z.string().optional(),
});
export type DmcEscalationStep = z.infer<typeof dmcEscalationStepSchema>;

// 14 BIM — NDA-safe bands (all L3).
export const dmcBiMetricsSchema = z.object({
  revenueBand: z.string().optional(),
  filesPerYearBand: z.string().optional(),
  averageTicketBand: z.string().optional(),
  seasonality: z.string().optional(),
});
export type DmcBiMetrics = z.infer<typeof dmcBiMetricsSchema>;

// --- Child entity (read) schemas -------------------------------------------

const childTimestamps = {
  id: z.uuid(),
  organizationId: z.uuid(),
  createdOn: z.string().nullable(),
  modifiedOn: z.string().nullable(),
  deletedOn: z.string().nullable(),
};

export const dmcOfficeSchema = z.object({
  ...childTimestamps,
  label: z.string().nullable(),
  streetAddress: z.string().nullable(),
  city: z.string().nullable(),
  stateOrRegion: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
  timezone: z.string().nullable(),
  officePhone: z.string().nullable(),
  email: z.string().nullable(),
  emergencyContactName: z.string().nullable(),
  emergencyContactPhone: z.string().nullable(),
});
export type DmcOffice = z.infer<typeof dmcOfficeSchema>;

export const dmcTeamMemberSchema = z.object({
  ...childTimestamps,
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  role: z.string().nullable(),
  email: z.string().nullable(),
  officePhone: z.string().nullable(),
  mobilePhone: z.string().nullable(),
  languages: z.array(z.string()),
  isCrisisTeam: z.boolean(),
  hasXthoryAccess: z.boolean(),
});
export type DmcTeamMember = z.infer<typeof dmcTeamMemberSchema>;

export const dmcInsurancePolicySchema = z.object({
  ...childTimestamps,
  policyType: z.string().nullable(),
  insurer: z.string().nullable(),
  policyNumber: z.string().nullable(),
  coverageAmount: z.string().nullable(),
  currency: z.string().nullable(),
  territory: z.string().nullable(),
  insurerHotline: z.string().nullable(),
  validFrom: z.string().nullable(),
  expiryDate: z.string().nullable(),
  documentRef: z.uuid().nullable(),
});
export type DmcInsurancePolicy = z.infer<typeof dmcInsurancePolicySchema>;

export const dmcBankAccountSchema = z.object({
  ...childTimestamps,
  invoicingEntity: z.string().nullable(),
  accountLabel: z.string().nullable(),
  bankName: z.string().nullable(),
  currency: z.string().nullable(),
  isPrimary: z.boolean(),
  paymentTerms: z.string().nullable(),
  vaultReference: z.string().nullable(),
});
export type DmcBankAccount = z.infer<typeof dmcBankAccountSchema>;

export const dmcLicenseSchema = z.object({
  ...childTimestamps,
  type: z.string().nullable(),
  name: z.string().nullable(),
  reference: z.string().nullable(),
  issuer: z.string().nullable(),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
  documentRef: z.uuid().nullable(),
});
export type DmcLicense = z.infer<typeof dmcLicenseSchema>;

export const dmcSourceMarketSchema = z.object({
  ...childTimestamps,
  market: z.string().nullable(),
  status: z.string().nullable(),
  revenueShareBand: z.string().nullable(),
  segments: z.array(z.string()),
  networkPresence: z.string().nullable(),
});
export type DmcSourceMarket = z.infer<typeof dmcSourceMarketSchema>;

export const dmcClientSchema = z.object({
  ...childTimestamps,
  name: z.string().nullable(),
  relation: z.string().nullable(),
  clientType: z.string().nullable(),
  tier: z.string().nullable(),
  marketRef: z.uuid().nullable(),
  ownerRef: z.uuid().nullable(),
});
export type DmcClient = z.infer<typeof dmcClientSchema>;

export const dmcRepresentationSchema = z.object({
  ...childTimestamps,
  firmName: z.string().nullable(),
  marketRef: z.uuid().nullable(),
  scope: z.string().nullable(),
  isExclusive: z.boolean(),
  feeModel: z.string().nullable(),
  contractStart: z.string().nullable(),
  contractEnd: z.string().nullable(),
  contactName: z.string().nullable(),
  contactEmail: z.string().nullable(),
  contactPhone: z.string().nullable(),
});
export type DmcRepresentation = z.infer<typeof dmcRepresentationSchema>;

export const dmcDocumentSchema = z.object({
  ...childTimestamps,
  title: z.string().nullable(),
  category: z.string().nullable(),
  version: z.string().nullable(),
  ownerRef: z.uuid().nullable(),
  link: z.string().nullable(),
  expiryDate: z.string().nullable(),
  lastVerifiedDate: z.string().nullable(),
});
export type DmcDocument = z.infer<typeof dmcDocumentSchema>;

// --- Parent + full profile (read) ------------------------------------------

export const dmcProfileSchema = z.object({
  organizationId: z.uuid(),
  // 01 CIL
  name: z.string().nullable(),
  tradingName: z.string().nullable(),
  companyIdNumber: z.string().nullable(),
  vatId: z.string().nullable(),
  legalForm: z.string().nullable(),
  countryOfIncorporation: z.string().nullable(),
  directors: z.array(dmcDirectorSchema),
  shareholding: z.array(dmcShareholderSchema),
  // 07 RES
  responsibilityStructure: dmcResponsibilityStructureSchema,
  // 11 DIG
  digitalPresence: dmcDigitalPresenceSchema,
  // 12 MPL
  marketingPlan: dmcMarketingPlanSchema,
  // 13 EMG
  emergencyLine: z.string().nullable(),
  crisisTeamRefs: z.array(z.string()),
  escalationLadder: z.array(dmcEscalationStepSchema),
  protocolReviewDate: z.string().nullable(),
  // 14 BIM
  biMetrics: dmcBiMetricsSchema,
  // 15 ELG
  memberStatus: z.string().nullable(),
  exclusiveDestination: z.string().nullable(),
  contractVersion: z.string().nullable(),
  ndaVersion: z.string().nullable(),
  xthoryStatus: z.string().nullable(),
  joinedOn: z.string().nullable(),
  createdOn: z.string().nullable(),
  modifiedOn: z.string().nullable(),
});
export type DmcProfileRow = z.infer<typeof dmcProfileSchema>;

// The full profile returned by `dmcProfile.find` — parent fields flattened with
// the child collections. `organizationId` is always present; the parent may not
// exist yet (fresh org), in which case parent fields come back as defaults.
export const dmcProfileFullSchema = dmcProfileSchema.extend({
  offices: z.array(dmcOfficeSchema),
  team: z.array(dmcTeamMemberSchema),
  insurancePolicies: z.array(dmcInsurancePolicySchema),
  bankAccounts: z.array(dmcBankAccountSchema),
  licenses: z.array(dmcLicenseSchema),
  sourceMarkets: z.array(dmcSourceMarketSchema),
  clients: z.array(dmcClientSchema),
  representations: z.array(dmcRepresentationSchema),
  documents: z.array(dmcDocumentSchema),
});
export type DmcProfileFull = z.infer<typeof dmcProfileFullSchema>;

// --- Save input ------------------------------------------------------------
// Field-level validation (spec §8, Phase 1 subset): email/URL syntax and
// expiry-not-in-past are enforced here; cross-record completeness rules are
// Phase 2 (data-quality). A blank string is allowed (fields are optional).

const optionalEmail = z
  .string()
  .trim()
  .refine((v) => v === '' || z.email().safeParse(v).success, 'Invalid email')
  .optional();

const optionalUrl = z
  .string()
  .trim()
  .refine((v) => v === '' || z.url().safeParse(v).success, 'Invalid URL')
  .optional();

// Child rows in the save payload carry an optional client-side id: existing rows
// keep their uuid; new rows omit it. The server upserts by id and soft-deletes
// any existing row absent from the payload.
const childId = z.uuid().optional();

export const dmcOfficeInput = z.object({
  id: childId,
  label: z.string().optional(),
  streetAddress: z.string().optional(),
  city: z.string().optional(),
  stateOrRegion: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  timezone: z.string().optional(),
  officePhone: z.string().optional(),
  email: optionalEmail,
  emergencyContactName: z.string().optional(),
  emergencyContactPhone: z.string().optional(),
});
export type DmcOfficeInput = z.infer<typeof dmcOfficeInput>;

export const dmcTeamMemberInput = z.object({
  id: childId,
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  role: z.string().optional(),
  email: optionalEmail,
  officePhone: z.string().optional(),
  mobilePhone: z.string().optional(),
  languages: z.array(z.string()).optional(),
  isCrisisTeam: z.boolean().optional(),
  hasXthoryAccess: z.boolean().optional(),
});
export type DmcTeamMemberInput = z.infer<typeof dmcTeamMemberInput>;

export const dmcInsurancePolicyInput = z.object({
  id: childId,
  policyType: z.string().optional(),
  insurer: z.string().optional(),
  policyNumber: z.string().optional(),
  coverageAmount: z.string().optional(),
  currency: z.string().optional(),
  territory: z.string().optional(),
  insurerHotline: z.string().optional(),
  validFrom: z.string().optional(),
  expiryDate: z.string().optional(),
  documentRef: z.uuid().optional(),
});
export type DmcInsurancePolicyInput = z.infer<typeof dmcInsurancePolicyInput>;

export const dmcBankAccountInput = z.object({
  id: childId,
  invoicingEntity: z.string().optional(),
  accountLabel: z.string().optional(),
  bankName: z.string().optional(),
  currency: z.string().optional(),
  isPrimary: z.boolean().optional(),
  paymentTerms: z.string().optional(),
  vaultReference: z.string().optional(),
});
export type DmcBankAccountInput = z.infer<typeof dmcBankAccountInput>;

export const dmcLicenseInput = z.object({
  id: childId,
  type: z.string().optional(),
  name: z.string().optional(),
  reference: z.string().optional(),
  issuer: z.string().optional(),
  validFrom: z.string().optional(),
  validUntil: z.string().optional(),
  documentRef: z.uuid().optional(),
});
export type DmcLicenseInput = z.infer<typeof dmcLicenseInput>;

export const dmcSourceMarketInput = z.object({
  id: childId,
  market: z.string().optional(),
  status: z.string().optional(),
  revenueShareBand: z.string().optional(),
  segments: z.array(z.string()).optional(),
  networkPresence: z.string().optional(),
});
export type DmcSourceMarketInput = z.infer<typeof dmcSourceMarketInput>;

export const dmcClientInput = z.object({
  id: childId,
  name: z.string().optional(),
  relation: z.string().optional(),
  clientType: z.string().optional(),
  tier: z.string().optional(),
  marketRef: z.uuid().optional(),
  ownerRef: z.uuid().optional(),
});
export type DmcClientInput = z.infer<typeof dmcClientInput>;

export const dmcRepresentationInput = z.object({
  id: childId,
  firmName: z.string().optional(),
  marketRef: z.uuid().optional(),
  scope: z.string().optional(),
  isExclusive: z.boolean().optional(),
  feeModel: z.string().optional(),
  contractStart: z.string().optional(),
  contractEnd: z.string().optional(),
  contactName: z.string().optional(),
  contactEmail: optionalEmail,
  contactPhone: z.string().optional(),
});
export type DmcRepresentationInput = z.infer<typeof dmcRepresentationInput>;

export const dmcDocumentInput = z.object({
  id: childId,
  title: z.string().optional(),
  category: z.string().optional(),
  version: z.string().optional(),
  ownerRef: z.uuid().optional(),
  link: optionalUrl,
  expiryDate: z.string().optional(),
  lastVerifiedDate: z.string().optional(),
});
export type DmcDocumentInput = z.infer<typeof dmcDocumentInput>;

// Parent (1:1 modules) patch — every field optional so the form can save any
// subset of modules.
export const dmcProfileParentInput = z.object({
  // 01 CIL
  name: z.string().optional(),
  tradingName: z.string().optional(),
  companyIdNumber: z.string().optional(),
  vatId: z.string().optional(),
  legalForm: z.string().optional(),
  countryOfIncorporation: z.string().optional(),
  directors: z.array(dmcDirectorSchema).optional(),
  shareholding: z.array(dmcShareholderSchema).optional(),
  // 07 RES
  responsibilityStructure: dmcResponsibilityStructureSchema.optional(),
  // 11 DIG
  digitalPresence: dmcDigitalPresenceSchema.optional(),
  // 12 MPL
  marketingPlan: dmcMarketingPlanSchema.optional(),
  // 13 EMG
  emergencyLine: z.string().optional(),
  crisisTeamRefs: z.array(z.string()).optional(),
  escalationLadder: z.array(dmcEscalationStepSchema).optional(),
  protocolReviewDate: z.string().optional(),
  // 14 BIM
  biMetrics: dmcBiMetricsSchema.optional(),
  // 15 ELG
  memberStatus: z.string().optional(),
  exclusiveDestination: z.string().optional(),
  contractVersion: z.string().optional(),
  ndaVersion: z.string().optional(),
  xthoryStatus: z.string().optional(),
  joinedOn: z.string().optional(),
});
export type DmcProfileParentInput = z.infer<typeof dmcProfileParentInput>;

// The whole-profile save. Every section is optional: the form sends only the
// modules the user touched. Child arrays, when present, are the authoritative
// full set for that module (rows absent from the array are soft-deleted).
export const saveDmcProfileInput = z.object({
  profile: dmcProfileParentInput.optional(),
  offices: z.array(dmcOfficeInput).optional(),
  team: z.array(dmcTeamMemberInput).optional(),
  insurancePolicies: z.array(dmcInsurancePolicyInput).optional(),
  bankAccounts: z.array(dmcBankAccountInput).optional(),
  licenses: z.array(dmcLicenseInput).optional(),
  sourceMarkets: z.array(dmcSourceMarketInput).optional(),
  clients: z.array(dmcClientInput).optional(),
  representations: z.array(dmcRepresentationInput).optional(),
  documents: z.array(dmcDocumentInput).optional(),
});
export type SaveDmcProfileInput = z.infer<typeof saveDmcProfileInput>;

// ===========================================================================
// Access control — field-level L1/L2/L3 (apps/auth/prd/dmc-partner-profile.md
// §4/§5, Phase 2). Single source of truth for which fields/modules are
// Restricted (L3) or Internal (L2); everything else is Consortium (L1).
// ===========================================================================

// Numeric ordering so "clearance >= field tier" comparisons are trivial.
const TIER_RANK: Record<DmcAccessTier, number> = { L1: 1, L2: 2, L3: 3 };

// Parent (1:1) fields whose tier is above L1. Keys are `dmcProfileSchema`
// field names; anything not listed is L1. (Doc §5 typical content.)
export const dmcParentFieldTiers: Partial<
  Record<keyof DmcProfileRow, DmcAccessTier>
> = {
  // L2 Internal — owning DMC + HQ only.
  companyIdNumber: 'L2', // registration
  vatId: 'L2',
  marketingPlan: 'L2',
  // L3 Restricted — DMC Principal + HQ Finance/Legal only.
  shareholding: 'L3',
  biMetrics: 'L3',
};

// Repeatable child collections whose tier is above L1 (whole-collection tier).
// Keys match the `DmcProfileFull` collection keys.
export const dmcChildCollectionTiers: Record<string, DmcAccessTier> = {
  insurancePolicies: 'L2',
  clients: 'L2',
  representations: 'L2',
  bankAccounts: 'L3', // account references / vault refs
};

// Individual fields whose tier is HIGHER than their container's — the doc (§3)
// puts some restricted fields inside an otherwise-lower module. Without this,
// whole-collection/parent-field stripping alone would leak them (e.g. a fee
// model on an L2 representation, a budget band on the L2 marketing plan, a
// revenue-share band on an L1 source market). Keys are the container (a parent
// object field OR a child-collection key); values map the nested field → its
// own tier. `find` blanks these for under-cleared readers; `save` rejects
// writing them above the caller's clearance.
export const dmcNestedFieldTiers: Record<
  string,
  Record<string, DmcAccessTier>
> = {
  marketingPlan: { budgetBand: 'L3' }, // 12 MPL — budget band (parent jsonb)
  representations: { feeModel: 'L3' }, // 10 REP — fee model (per row)
  sourceMarkets: { revenueShareBand: 'L2' }, // 08 MKT — revenue share (per row)
};

// Max tier a role may READ. Roles absent from the map get L1 (directory view).
const ROLE_READ_CLEARANCE: Partial<Record<Role, DmcAccessTier>> = {
  [RoleConst.HqAdmin]: 'L3',
  [RoleConst.HqFinanceLegal]: 'L3',
  [RoleConst.DmcPrincipal]: 'L3',
  [RoleConst.DmcEditor]: 'L2',
  [RoleConst.ConsortiumMember]: 'L1',
  // Existing platform roles that reached the profile in Phase 1 keep L3 so the
  // admin experience is unchanged.
  [RoleConst.Admin]: 'L3',
  [RoleConst.SuperFinance]: 'L3',
  [RoleConst.Consulting]: 'L2',
};

// Roles that may EDIT (write) the profile at all. HQ Finance/Legal and
// Consortium Member are read-only (doc §3). Max WRITE tier mirrors read for the
// editing roles.
const ROLE_WRITE_CLEARANCE: Partial<Record<Role, DmcAccessTier>> = {
  [RoleConst.HqAdmin]: 'L3',
  [RoleConst.DmcPrincipal]: 'L3',
  [RoleConst.DmcEditor]: 'L2',
  [RoleConst.Admin]: 'L3',
  [RoleConst.SuperFinance]: 'L3',
  [RoleConst.Consulting]: 'L2',
};

const maxTier = (
  roles: Role[],
  map: Partial<Record<Role, DmcAccessTier>>
): number =>
  roles.reduce((best, r) => {
    const t = map[r];
    return t ? Math.max(best, TIER_RANK[t]) : best;
  }, 0);

export const dmcReadClearance = (roles: Role[]): number =>
  // Default to L1 (directory view) for any authenticated caller.
  Math.max(1, maxTier(roles, ROLE_READ_CLEARANCE));

export const dmcWriteClearance = (roles: Role[]): number =>
  maxTier(roles, ROLE_WRITE_CLEARANCE);

export const dmcCanEdit = (roles: Role[]): boolean =>
  dmcWriteClearance(roles) > 0;

// True if the caller may see a given tier.
export const canAccessTier = (
  clearance: number,
  tier: DmcAccessTier
): boolean => clearance >= TIER_RANK[tier];

// The L3 parent-field + child-collection keys served for a given clearance —
// used by read-logging to record exactly which restricted fields were exposed.
export const l3FieldsForClearance = (clearance: number): string[] => {
  if (clearance < TIER_RANK.L3) return [];
  return [
    ...Object.entries(dmcParentFieldTiers)
      .filter(([, t]) => t === 'L3')
      .map(([k]) => k),
    ...Object.entries(dmcChildCollectionTiers)
      .filter(([, t]) => t === 'L3')
      .map(([k]) => k),
  ];
};

// ===========================================================================
// Consortium directory (apps/auth/prd/dmc-partner-profile.md §4/FR-05, Phase 2).
// A read-only, cross-org view exposing L1 (Consortium) fields only. This is the
// feature's ONLY cross-org read — every other DmcProfile procedure is scoped to
// the caller's own org. The server always projects to L1 here, regardless of the
// caller's own clearance (a member sees only L1 of OTHER members).
// ===========================================================================

// One row in the searchable directory list.
export const dmcDirectorySummarySchema = z.object({
  organizationId: z.uuid(),
  // Trading name preferred, falling back to legal name; may be the org title.
  name: z.string().nullable(),
  exclusiveDestination: z.string().nullable(),
  memberStatus: z.string().nullable(),
  markets: z.array(z.string()),
  segments: z.array(z.string()),
  networkPresence: z.array(z.string()),
  countries: z.array(z.string()),
  officeCount: z.number(),
});
export type DmcDirectorySummary = z.infer<typeof dmcDirectorySummarySchema>;

// L1-only office fields for the detail view (subset of `dmcOfficeSchema`).
export const dmcDirectoryOfficeSchema = z.object({
  label: z.string().nullable(),
  city: z.string().nullable(),
  stateOrRegion: z.string().nullable(),
  country: z.string().nullable(),
  timezone: z.string().nullable(),
  officePhone: z.string().nullable(),
  email: z.string().nullable(),
});
export type DmcDirectoryOffice = z.infer<typeof dmcDirectoryOfficeSchema>;

// L1-only team fields (name/role/email/languages/phones are all L1).
export const dmcDirectoryTeamMemberSchema = z.object({
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  role: z.string().nullable(),
  email: z.string().nullable(),
  officePhone: z.string().nullable(),
  mobilePhone: z.string().nullable(),
  languages: z.array(z.string()),
});
export type DmcDirectoryTeamMember = z.infer<
  typeof dmcDirectoryTeamMemberSchema
>;

// L1-only source-market fields — `revenueShareBand` is deliberately EXCLUDED
// (above L1 per doc §3), even though the whole `sourceMarkets` collection is not
// in `dmcChildCollectionTiers`.
export const dmcDirectoryMarketSchema = z.object({
  market: z.string().nullable(),
  status: z.string().nullable(),
  segments: z.array(z.string()),
  networkPresence: z.string().nullable(),
});
export type DmcDirectoryMarket = z.infer<typeof dmcDirectoryMarketSchema>;

// The full L1 detail returned when a member opens another member's card.
export const dmcDirectoryDetailSchema = z.object({
  organizationId: z.uuid(),
  name: z.string().nullable(),
  tradingName: z.string().nullable(),
  exclusiveDestination: z.string().nullable(),
  memberStatus: z.string().nullable(),
  emergencyLine: z.string().nullable(),
  digitalPresence: dmcDigitalPresenceSchema,
  offices: z.array(dmcDirectoryOfficeSchema),
  team: z.array(dmcDirectoryTeamMemberSchema),
  sourceMarkets: z.array(dmcDirectoryMarketSchema),
});
export type DmcDirectoryDetail = z.infer<typeof dmcDirectoryDetailSchema>;

// Directory search input — a single substring query over name / destination /
// market / segment / network.
export const dmcDirectorySearchInput = z.object({
  query: z.string().optional(),
});
export type DmcDirectorySearchInput = z.infer<typeof dmcDirectorySearchInput>;

// Detail lookup input.
export const dmcDirectoryEntryInput = z.object({
  organizationId: z.uuid(),
});
export type DmcDirectoryEntryInput = z.infer<typeof dmcDirectoryEntryInput>;
